/**
 * Canvas Side Panel
 * Slide-in panel for viewing and editing artifacts (code, documents, tables, diagrams)
 */

import { store, setActiveArtifact, closeArtifactPanel, setArtifactPanelWidth } from '../lib/appState.js';
import { updateArtifactContent, restoreArtifactVersion, getArtifactsForConversation } from '../lib/artifacts.js';
import { highlight } from '../lib/highlighter.js';
import { showToast } from '../lib/toast.js';
import { db } from '../lib/db.js';

const typeIcons = {
  code: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`,
  document: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`,
  table: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>`,
  diagram: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>`,
};

const closeIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const backIcon = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>`;
const downloadIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
const copyIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
const historyIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

let isEditing = false;
let showVersions = false;
let editingTitle = false;

/**
 * Render the canvas side panel
 */
export function renderCanvasPanel(state) {
  const { artifacts } = state;
  if (!artifacts.panelOpen || !artifacts.activeId) return '';

  const artifact = artifacts.items[artifacts.activeId];
  if (!artifact) return '';

  const isMobile = window.innerWidth < 768;
  const convArtifacts = getArtifactsForConversation(artifact.conversationId);

  return `
    <div class="canvas-panel ${isMobile ? 'canvas-panel-mobile' : ''}"
         style="${isMobile ? '' : `width: ${artifacts.panelWidth}px`}">
      ${isMobile ? '' : '<div class="canvas-resize-handle" data-action="canvas-resize"></div>'}
      <div class="canvas-panel-header">
        <div class="canvas-panel-header-left">
          ${isMobile ? `<button class="canvas-header-btn" data-action="close-canvas" aria-label="Back">${backIcon}</button>` : ''}
          <span class="canvas-panel-type-icon">${typeIcons[artifact.type] || typeIcons.code}</span>
          <span class="canvas-panel-title" data-action="edit-artifact-title" title="Click to rename">${escapeHtml(artifact.title)}</span>
        </div>
        <div class="canvas-panel-header-right">
          ${artifact.versions.length > 1 ? `
            <button class="canvas-header-btn" data-action="toggle-versions" title="Version history (${artifact.versions.length})">
              ${historyIcon}
              <span class="version-badge">${artifact.versions.length}</span>
            </button>
          ` : ''}
          <button class="canvas-header-btn" data-action="copy-artifact" title="Copy">${copyIcon}</button>
          <button class="canvas-header-btn" data-action="download-artifact" title="Download">${downloadIcon}</button>
          ${isMobile ? '' : `<button class="canvas-header-btn" data-action="close-canvas" aria-label="Close">${closeIcon}</button>`}
        </div>
      </div>
      ${convArtifacts.length > 1 ? renderArtifactTabs(convArtifacts, artifact.id) : ''}
      ${showVersions && artifact.versions.length > 1 ? renderVersionHistory(artifact) : ''}
      <div class="canvas-panel-body">
        ${renderArtifactContent(artifact)}
      </div>
    </div>
  `;
}

function renderArtifactTabs(artifacts, activeId) {
  return `
    <div class="canvas-tabs">
      ${artifacts.map(a => `
        <button class="canvas-tab ${a.id === activeId ? 'active' : ''}"
                data-action="switch-artifact" data-artifact-id="${a.id}">
          <span class="canvas-tab-icon">${typeIcons[a.type] || typeIcons.code}</span>
          <span class="canvas-tab-label">${escapeHtml(a.title.length > 20 ? a.title.slice(0, 18) + '...' : a.title)}</span>
        </button>
      `).join('')}
    </div>
  `;
}

function renderVersionHistory(artifact) {
  return `
    <div class="canvas-versions">
      <div class="canvas-versions-header">Version History</div>
      ${artifact.versions.map((v, i) => `
        <button class="canvas-version-item ${v.content === artifact.content ? 'active' : ''}"
                data-action="restore-version" data-version-index="${i}">
          <span>v${i + 1}</span>
          <span class="canvas-version-time">${formatTime(v.timestamp)}</span>
        </button>
      `).join('')}
    </div>
  `;
}

function renderArtifactContent(artifact) {
  switch (artifact.type) {
    case 'code':
    case 'diagram':
      return renderCodeEditor(artifact);
    case 'table':
      return renderTableView(artifact);
    case 'document':
      return renderDocumentView(artifact);
    default:
      return renderCodeEditor(artifact);
  }
}

function renderCodeEditor(artifact) {
  const lines = artifact.content.split('\n');
  const lineNumbers = lines.map((_, i) => `<span class="line-num">${i + 1}</span>`).join('');
  const langLabel = artifact.language || (artifact.type === 'diagram' ? 'mermaid' : 'text');

  if (isEditing) {
    return `
      <div class="canvas-editor">
        <div class="canvas-editor-toolbar">
          <span class="canvas-editor-lang">${escapeHtml(langLabel)}</span>
          <div class="canvas-editor-actions">
            <button class="canvas-toolbar-btn" data-action="save-artifact-edit">Save</button>
            <button class="canvas-toolbar-btn" data-action="cancel-artifact-edit">Cancel</button>
          </div>
        </div>
        <div class="canvas-code-edit-area">
          <div class="canvas-line-numbers">${lineNumbers}</div>
          <textarea class="canvas-code-textarea" id="canvas-editor-textarea" spellcheck="false">${escapeHtml(artifact.content)}</textarea>
        </div>
      </div>
    `;
  }

  const highlighted = artifact.language ? highlight(escapeHtml(artifact.content), artifact.language) : escapeHtml(artifact.content);

  return `
    <div class="canvas-editor">
      <div class="canvas-editor-toolbar">
        <span class="canvas-editor-lang">${escapeHtml(langLabel)}</span>
        <div class="canvas-editor-actions">
          <button class="canvas-toolbar-btn" data-action="edit-artifact">Edit</button>
          ${artifact.type === 'code' ? `<button class="canvas-toolbar-btn" data-action="run-artifact">Run</button>` : ''}
        </div>
      </div>
      <div class="canvas-code-view">
        <div class="canvas-line-numbers">${lineNumbers}</div>
        <pre class="canvas-code-pre"><code class="${artifact.language || ''}">${highlighted}</code></pre>
      </div>
    </div>
  `;
}

function renderTableView(artifact) {
  const rows = artifact.content.trim().split('\n');
  if (rows.length < 2) return `<pre>${escapeHtml(artifact.content)}</pre>`;

  const isSeparator = /^\|[\s\-:]+([\s\-:]+\|)+$/.test(rows[1]);
  if (!isSeparator) return `<pre>${escapeHtml(artifact.content)}</pre>`;

  const headers = rows[0].split('|').filter(Boolean).map(h => h.trim());
  const dataRows = rows.slice(2).map(r => r.split('|').filter(Boolean).map(c => c.trim()));

  return `
    <div class="canvas-table-view">
      <div class="canvas-table-actions">
        <button class="canvas-toolbar-btn" data-action="copy-csv">Copy CSV</button>
        <button class="canvas-toolbar-btn" data-action="copy-markdown-table">Copy Markdown</button>
      </div>
      <div class="canvas-table-scroll">
        <table class="canvas-artifact-table">
          <thead>
            <tr>${headers.map((h, i) => `<th data-action="sort-column" data-col="${i}">${escapeHtml(h)} <span class="sort-indicator"></span></th>`).join('')}</tr>
          </thead>
          <tbody>
            ${dataRows.map(row => `<tr>${row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function renderDocumentView(artifact) {
  if (isEditing) {
    return `
      <div class="canvas-document-edit">
        <div class="canvas-editor-toolbar">
          <span class="canvas-editor-lang">Document</span>
          <div class="canvas-editor-actions">
            <button class="canvas-toolbar-btn" data-action="save-artifact-edit">Save</button>
            <button class="canvas-toolbar-btn" data-action="cancel-artifact-edit">Cancel</button>
          </div>
        </div>
        <div class="canvas-doc-editable" contenteditable="true" id="canvas-doc-editor">${renderSimpleMarkdown(artifact.content)}</div>
      </div>
    `;
  }

  return `
    <div class="canvas-document-view">
      <div class="canvas-editor-toolbar">
        <span class="canvas-editor-lang">Document</span>
        <div class="canvas-editor-actions">
          <button class="canvas-toolbar-btn" data-action="edit-artifact">Edit</button>
        </div>
      </div>
      <div class="canvas-doc-content markdown-body">${renderSimpleMarkdown(artifact.content)}</div>
    </div>
  `;
}

function renderSimpleMarkdown(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^# (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');
  return `<p>${html}</p>`;
}

/**
 * Handle all canvas panel interactions
 */
export function handleCanvasAction(e) {
  const target = e.target.closest('[data-action]');
  if (!target) return;
  const action = target.dataset.action;

  const state = store.getState();
  const artifact = state.artifacts.items[state.artifacts.activeId];

  switch (action) {
    case 'close-canvas':
      isEditing = false;
      showVersions = false;
      closeArtifactPanel();
      break;

    case 'switch-artifact':
      isEditing = false;
      showVersions = false;
      setActiveArtifact(target.dataset.artifactId);
      break;

    case 'open-artifact':
      isEditing = false;
      showVersions = false;
      setActiveArtifact(target.dataset.artifactId);
      break;

    case 'copy-artifact':
      if (artifact) {
        navigator.clipboard.writeText(artifact.content).then(() => showToast('Copied to clipboard', 'success'));
      }
      break;

    case 'download-artifact':
      if (artifact) downloadArtifact(artifact);
      break;

    case 'edit-artifact':
      isEditing = true;
      // trigger re-render by touching state
      store.setState({ artifacts: { ...state.artifacts } });
      requestAnimationFrame(() => {
        const textarea = document.getElementById('canvas-editor-textarea');
        if (textarea) textarea.focus();
        const editable = document.getElementById('canvas-doc-editor');
        if (editable) editable.focus();
      });
      break;

    case 'save-artifact-edit': {
      if (!artifact) break;
      let newContent;
      if (artifact.type === 'document') {
        const editable = document.getElementById('canvas-doc-editor');
        newContent = editable ? editable.innerText : artifact.content;
      } else {
        const textarea = document.getElementById('canvas-editor-textarea');
        newContent = textarea ? textarea.value : artifact.content;
      }
      if (newContent !== artifact.content) {
        updateArtifactContent(artifact.id, newContent);
      }
      isEditing = false;
      store.setState({ artifacts: { ...state.artifacts } });
      showToast('Artifact saved', 'success');
      break;
    }

    case 'cancel-artifact-edit':
      isEditing = false;
      store.setState({ artifacts: { ...state.artifacts } });
      break;

    case 'run-artifact':
      showToast('Execution not available', 'info');
      break;

    case 'toggle-versions':
      showVersions = !showVersions;
      store.setState({ artifacts: { ...state.artifacts } });
      break;

    case 'restore-version': {
      const versionIndex = parseInt(target.dataset.versionIndex, 10);
      if (artifact && !isNaN(versionIndex)) {
        restoreArtifactVersion(artifact.id, versionIndex);
        showVersions = false;
        showToast(`Restored to v${versionIndex + 1}`, 'success');
      }
      break;
    }

    case 'edit-artifact-title': {
      if (!artifact || editingTitle) break;
      editingTitle = true;
      const titleEl = target;
      const currentTitle = artifact.title;
      titleEl.outerHTML = `<input class="canvas-title-input" id="canvas-title-input" type="text" value="${escapeAttr(currentTitle)}" />`;
      const input = document.getElementById('canvas-title-input');
      if (input) {
        input.focus();
        input.select();
        const saveTitle = () => {
          editingTitle = false;
          const newTitle = input.value.trim() || currentTitle;
          store.setState({
            artifacts: {
              items: {
                ...state.artifacts.items,
                [artifact.id]: { ...artifact, title: newTitle, updatedAt: Date.now() },
              },
            },
          });
          db.saveArtifact({ ...artifact, title: newTitle, updatedAt: Date.now() });
        };
        input.addEventListener('blur', saveTitle);
        input.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter') { saveTitle(); input.blur(); }
          if (ev.key === 'Escape') { editingTitle = false; input.blur(); }
        });
      }
      break;
    }

    case 'sort-column': {
      if (!artifact) break;
      const colIndex = parseInt(target.dataset.col, 10);
      sortTable(colIndex);
      break;
    }

    case 'copy-csv': {
      if (!artifact) break;
      const csv = tableToCSV(artifact.content);
      navigator.clipboard.writeText(csv).then(() => showToast('CSV copied', 'success'));
      break;
    }

    case 'copy-markdown-table': {
      if (!artifact) break;
      navigator.clipboard.writeText(artifact.content).then(() => showToast('Markdown copied', 'success'));
      break;
    }

    // Legacy inline canvas actions
    case 'download-canvas': {
      const container = target.closest('.canvas-container');
      if (!container) break;
      const code = container.querySelector('code')?.textContent || '';
      const filename = target.dataset.filename || 'download.txt';
      downloadText(code, filename);
      break;
    }
    case 'export-csv': {
      const container = target.closest('.canvas-container');
      const table = container?.querySelector('table');
      if (table) {
        const csv = tableToCsvFromDom(table);
        downloadText(csv, 'export.csv');
      }
      break;
    }
    case 'clear-form': {
      const container = target.closest('.canvas-container');
      container?.querySelector('form')?.reset();
      break;
    }
    case 'submit-form': {
      const container = target.closest('.canvas-container');
      const form = container?.querySelector('form');
      if (form) {
        const data = Object.fromEntries(new FormData(form));
        console.log('Form submitted:', data);
      }
      break;
    }
  }
}

function sortTable(colIndex) {
  const tableEl = document.querySelector('.canvas-artifact-table');
  if (!tableEl) return;

  const tbody = tableEl.querySelector('tbody');
  const rows = Array.from(tbody.querySelectorAll('tr'));

  // Toggle sort direction
  const th = tableEl.querySelectorAll('th')[colIndex];
  const ascending = th.dataset.sort !== 'asc';
  tableEl.querySelectorAll('th').forEach(h => { h.dataset.sort = ''; h.querySelector('.sort-indicator').textContent = ''; });
  th.dataset.sort = ascending ? 'asc' : 'desc';
  th.querySelector('.sort-indicator').textContent = ascending ? ' \u25B2' : ' \u25BC';

  rows.sort((a, b) => {
    const aVal = a.cells[colIndex]?.textContent?.trim() || '';
    const bVal = b.cells[colIndex]?.textContent?.trim() || '';
    const aNum = parseFloat(aVal);
    const bNum = parseFloat(bVal);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return ascending ? aNum - bNum : bNum - aNum;
    }
    return ascending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  rows.forEach(r => tbody.appendChild(r));
}

function tableToCSV(markdownTable) {
  const rows = markdownTable.trim().split('\n');
  return rows
    .filter(r => !/^\|[\s\-:]+\|$/.test(r))
    .map(row => {
      return row.split('|').filter(Boolean).map(cell => {
        let text = cell.trim();
        if (text.includes(',') || text.includes('"')) {
          text = `"${text.replace(/"/g, '""')}"`;
        }
        return text;
      }).join(',');
    })
    .join('\n');
}

function tableToCsvFromDom(table) {
  const rows = [];
  for (const row of table.rows) {
    const cells = [];
    for (const cell of row.cells) {
      let text = cell.textContent;
      if (text.includes(',') || text.includes('"')) {
        text = `"${text.replace(/"/g, '""')}"`;
      }
      cells.push(text);
    }
    rows.push(cells.join(','));
  }
  return rows.join('\n');
}

function downloadArtifact(artifact) {
  const ext = getExtension(artifact.language, artifact.type);
  const filename = artifact.title.replace(/[^a-z0-9._-]/gi, '_').slice(0, 50) + (artifact.title.includes('.') ? '' : `.${ext}`);
  const mimeType = artifact.type === 'document' ? 'text/markdown' : 'text/plain';
  downloadText(artifact.content, filename, mimeType);
}

function downloadText(content, filename, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function getExtension(lang, type) {
  if (type === 'table') return 'md';
  if (type === 'document') return 'md';
  if (type === 'diagram') return 'mmd';
  const map = {
    javascript: 'js', typescript: 'ts', python: 'py', rust: 'rs',
    go: 'go', java: 'java', html: 'html', css: 'css',
    json: 'json', yaml: 'yml', sql: 'sql', bash: 'sh', shell: 'sh',
  };
  return map[lang?.toLowerCase()] || 'txt';
}

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function escapeAttr(text) {
  if (!text) return '';
  return text.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Setup drag-to-resize for the canvas panel
 */
export function setupCanvasResize(container) {
  let isResizing = false;
  let startX = 0;
  let startWidth = 0;

  container.addEventListener('mousedown', (e) => {
    if (!e.target.closest('.canvas-resize-handle')) return;
    isResizing = true;
    startX = e.clientX;
    startWidth = store.getState().artifacts.panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  });

  document.addEventListener('mousemove', (e) => {
    if (!isResizing) return;
    const diff = startX - e.clientX;
    const newWidth = Math.max(320, Math.min(startWidth + diff, window.innerWidth * 0.7));
    const panel = container.querySelector('.canvas-panel');
    if (panel) panel.style.width = newWidth + 'px';
  });

  document.addEventListener('mouseup', () => {
    if (!isResizing) return;
    isResizing = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    const panel = container.querySelector('.canvas-panel');
    if (panel) {
      const width = parseInt(panel.style.width, 10);
      if (width) {
        setArtifactPanelWidth(width);
        localStorage.setItem('chorus-canvas-width', String(width));
      }
    }
  });
}

/**
 * Load persisted panel width
 */
export function loadCanvasWidth() {
  const saved = localStorage.getItem('chorus-canvas-width');
  if (saved) {
    const width = parseInt(saved, 10);
    if (width >= 320 && width <= 1200) {
      setArtifactPanelWidth(width);
    }
  }
}
